import React, { useState } from 'react';
import { pendenciasOnboarding, type RascunhoSessaoOnboarding, type SessaoOnboarding } from './onboardingPresenca';
import { useOnboardingSessoes } from './useOnboardingSessoes';
import SessaoOnboardingModal from './SessaoOnboardingModal';
import SessoesOnboardingModal from './SessoesOnboardingModal';

interface PresencaOnboardingResumoProps {
  documentoId: string;
  versao: number;
  /** Registro aberto de fora (ao fechar a apresentação ou em "Registrar esta sessão"). */
  rascunho?: RascunhoSessaoOnboarding | null;
  onRascunhoConsumido?: () => void;
}

const BOTAO =
  'rounded-full border border-raro-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-raro-gray-700 hover:border-primary-300 hover:text-primary-700';

/** Só SI, no cartão do documento de onboarding: resumo da presença, histórico e registrar sessão. */
export default function PresencaOnboardingResumo({
  documentoId,
  versao,
  rascunho = null,
  onRascunhoConsumido,
}: PresencaOnboardingResumoProps) {
  const { data: sessoes = [] } = useOnboardingSessoes(documentoId);
  const [verSessoes, setVerSessoes] = useState(false);
  const [editando, setEditando] = useState<SessaoOnboarding | 'nova' | null>(null);
  const aberto = rascunho ? 'nova' : editando;

  const idsCompletaram = Array.from(
    new Set(sessoes.flatMap((s) => s.participantes.filter((p) => p.presenca === 'completa').map((p) => p.colaboradorId)))
  );
  const completaram = idsCompletaram.length;
  const pendentes = pendenciasOnboarding(sessoes);
  const parciais = pendentes.filter((p) => p.presenca === 'parcial').length;
  const ultima = sessoes[0];

  const fechar = () => {
    setEditando(null);
    onRascunhoConsumido?.();
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-raro-gray-50 px-3 py-2.5">
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-raro-gray-600">
        <span>
          <b className="text-raro-gray-900">{sessoes.length}</b> sessão(ões)
          {ultima ? ` · última em ${ultima.data.split('-').reverse().slice(0, 2).join('/')}` : ''}
        </span>
        <span>
          <b className="text-raro-gray-900">{completaram}</b> participaram de tudo
        </span>
        {pendentes.length > 0 && (
          <span className="font-semibold text-amber-800">
            {parciais > 0 ? `${parciais} incompleto(s)` : ''}
            {parciais > 0 && pendentes.length - parciais > 0 ? ' · ' : ''}
            {pendentes.length - parciais > 0 ? `${pendentes.length - parciais} faltou(aram)` : ''}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={BOTAO} onClick={() => setVerSessoes(true)}>
          Ver sessões
        </button>
        <button type="button" className={BOTAO} onClick={() => setEditando('nova')}>
          + Registrar sessão
        </button>
      </div>

      <SessoesOnboardingModal
        sessoes={sessoes}
        isOpen={verSessoes && !aberto}
        onClose={() => setVerSessoes(false)}
        onAbrir={(sessao) => setEditando(sessao)}
        onNova={() => setEditando('nova')}
      />
      {aberto && (
        <SessaoOnboardingModal
          key={aberto === 'nova' ? 'nova' : aberto.id}
          documentoId={documentoId}
          versao={versao}
          sessao={aberto === 'nova' ? null : aberto}
          rascunho={aberto === 'nova' ? rascunho : null}
          jaCompletaram={idsCompletaram}
          isOpen
          onClose={fechar}
        />
      )}
    </div>
  );
}
